import { Dispatch } from 'redux';
import { MyrAPIService } from '../../api/myr/myrAPI';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../app/common/errorResolution/errorResolution';
import { ErrorResolutionActions } from '../../app/common/errorResolution/errorResolutionActions';
import { NotificationsActions } from '../../app/common/notifications/notificationsActions';
import { NotificationAction } from '../../app/common/notifications/notificationsActionType';
import { IAddress, IAddressRequest, IAddressUpdateRequest } from './address/address.interface';
import { AddressesAction, AddressesActionType } from './addressesActionType';
import { AddressesCache } from './addressesCache';
import { MyrAPIEndpoints } from '../../api/myr/myrAPIEndpoints.constant';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { IMasterAddress } from '../../api/addressSearch/addressSearch';
import ErrorRedirectService from '../../routes/error/errorRedirect.service';

export class AddressesActions {
    public static addAddress(newAddress: IAddressRequest): any {
        const endpoint = MyrAPIEndpoints.ADDRESS;

        const request = () => ({
            payload: {},
            type: AddressesActionType.ADD_ADDRESS_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            address: IAddress,
        ) => {
            dispatch({
                payload: { address },
                type: AddressesActionType.ADD_ADDRESS_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.address.add.success'),
                ),
            );
            dispatch(AddressesActions.getAddresses());
            return address;
        };

        const resolveError = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            dispatch({
                payload: { errorResolution },
                type: AddressesActionType.ADD_ADDRESS_FAILURE,
            });

            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(AddressesActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.address.add.failure',
                        { challengerPid: newAddress.challengeData?.cpid },
                    ),
                );
            }
        };

        return (dispatch: Dispatch<AddressesAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<IAddress>(endpoint, 'POST', newAddress)
                .then((json: IAddress) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static deleteAddress(addressUuid: string): any {
        const endpoint = `${MyrAPIEndpoints.ADDRESS}/${addressUuid}`;

        const request = () => ({
            payload: {},
            type: AddressesActionType.REMOVE_ADDRESS_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            json: { success: boolean },
        ) => {
            dispatch({
                // Response body should always have success: true
                payload: { addressUuid },
                type: AddressesActionType.REMOVE_ADDRESS_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.address.delete.success'),
                ),
            );
            dispatch(AddressesActions.getAddresses());
        };

        const resolveError = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(AddressesActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.address.delete.failure',
                    ),
                );
            }

            dispatch({
                payload: { errorResolution },
                type: AddressesActionType.REMOVE_ADDRESS_FAILURE,
            });
        };

        return (dispatch: Dispatch<AddressesAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<{ success: boolean }>(endpoint, 'DELETE')
                .then((json: { success: boolean }) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static getAddresses(): any {
        const endpoint = MyrAPIEndpoints.ADDRESS;
        const key = 'addresses';

        const request = () => ({
            payload: {},
            type: AddressesActionType.GET_ADDRESSES_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            addresses: IAddress[],
        ) => {
            addresses.sort((a, b) => {
                return a['isShipping'] ? -1 : b['isShipping'] ? 1 : 0;
            });
            AddressesCache.store(key, addresses);
            dispatch({
                payload: { addresses },
                type: AddressesActionType.GET_ADDRESSES_SUCCESS,
            });
        };

        const resolveError = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
            }

            dispatch({
                payload: { errorResolution },
                type: AddressesActionType.GET_ADDRESSES_FAILURE,
            });
        };

        return (dispatch: Dispatch<AddressesAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<IAddress[]>(endpoint, 'GET')
                .then((json: IAddress[]) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static setDefaultShipping(address: IAddress, isShipping: boolean) {
        return AddressesActions.updateAddress(address.addressUuid, {
            isShipping,
            challengeData: address.challengeData,
        });
    }

    public static updateAddress(addressUuid: string, updatedAddress: IAddressUpdateRequest): any {
        const endpoint = `${MyrAPIEndpoints.ADDRESS}/${addressUuid}`;

        const request = () => ({
            payload: {},
            type: AddressesActionType.UPDATE_ADDRESS_REQUEST,
        });

        const resolveSuccess = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            address: IAddress,
        ) => {
            dispatch({
                payload: { address },
                type: AddressesActionType.UPDATE_ADDRESS_SUCCESS,
            });
            dispatch(
                NotificationsActions.showSuccess(
                    TranslationHelpers.getFormattedMessage('notifications.address.update.success'),
                ),
            );
            dispatch(AddressesActions.getAddresses());
        };

        const resolveError = (
            dispatch: Dispatch<AddressesAction | NotificationAction>,
            errorResolution: ErrorResolution,
        ) => {
            if (errorResolution.type === ErrorResolutionTypes.RE_AUTH) {
                ErrorRedirectService.reAuth();
                dispatch(AddressesActions.closeForm());
            } else {
                dispatch(
                    ErrorResolutionActions.processErrorResolution(
                        errorResolution,
                        'notifications.address.update.failure',
                        { challengerPid: updatedAddress.challengeData?.cpid },
                    ),
                );
            }

            dispatch({
                payload: { errorResolution },
                type: AddressesActionType.UPDATE_ADDRESS_FAILURE,
            });
        };

        return (dispatch: Dispatch<AddressesAction>) => {
            dispatch(request());
            return MyrAPIService.sendRequest<IAddress>(endpoint, 'PUT', updatedAddress)
                .then((json: IAddress) => resolveSuccess(dispatch, json))
                .catch((errorResolution: ErrorResolution) =>
                    resolveError(dispatch, errorResolution),
                );
        };
    }

    public static openForm(): AddressesAction {
        return {
            payload: {},
            type: AddressesActionType.OPEN_ADDRESS_FORM,
        };
    }

    public static closeForm(): AddressesAction {
        return {
            payload: {},
            type: AddressesActionType.CLOSE_ADDRESS_FORM,
        };
    }

    public static setMasterAddress(masterAddress: IMasterAddress | null): AddressesAction {
        return {
            type: AddressesActionType.SET_MASTER_ADDRESS_REQUEST,
            payload: { masterAddress },
        };
    }

    public static hiddenUpdatedSuccessInfo(): AddressesAction {
        return {
            payload: {},
            type: AddressesActionType.HIDDEN_ADDRESSES_UPDATED_INFO,
        };
    }

    public static changeStateInputDisabled(status: boolean): AddressesAction {
        return {
            payload: status,
            type: AddressesActionType.CHANGE_STATE_INPUT_DISABLED,
        };
    }
}
